/**
 * The rundown tree: a virtualised (TanStack Virtual), keyboard-first,
 * drag-and-drop (dnd-kit) list of Segment → Part → Piece rows.
 *
 * - **Keys** are `lib/editor/keyboard.ts`'s reducer, run on the focused tree
 *   (`role="tree"`, the focused row as `aria-activedescendant`). Focus,
 *   selection and inline editing are a controlled `nav` prop, because the
 *   view needs them too (the inspector, presence).
 * - **Commands** (create, delete, undo, redo, collapse) are events: what
 *   they mean — a patch, a confirmation — is the view's.
 * - **Drag**: a row, or the whole selection when the dragged row is in it,
 *   is dragged; the pointer's position over the row under it picks before /
 *   after / inside (`positionFromPointer`), `canDrop` (the view, over the
 *   domain's parent rules) says whether that is allowed, and the row shows it.
 * - **Context menu**: right-click focuses the row and offers new rows,
 *   rename and delete.
 *
 * Presentational: rows and presence come in as view models.
 */
import { DndContext, type DragEndEvent, pointerWithin, type DragMoveEvent, DragOverlay, type DragStartEvent, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { type DropPosition, type DropTarget, positionFromPointer } from '../../lib/editor/drop.js';
import { createCommandFor, type NavState, navRows, reduceClick, reduceKey, type TreeCommand } from '../../lib/editor/keyboard.js';
import type { RowPresence } from '../../lib/editor/presence.js';
import { displayName, type FlatRow, type TreeKind } from '../../lib/editor/viewModel.js';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuTrigger } from '../ui/context-menu.js';
import { TreeRow } from './TreeRow.js';

/** Row height in px: the `h-8` of `TreeRow`. */
export const ROW_HEIGHT = 32;

const NO_PRESENCE: readonly RowPresence[] = [];

/** Which kind a row of each kind may be dropped INTO (the domain's parent rules, `ALLOWED_PARENT_KINDS`, as the tree shows them). */
const NESTS: Readonly<Record<TreeKind, TreeKind | null>> = { segment: 'part', part: 'piece', piece: null };

export type RundownTreeProps = {
  readonly rows: readonly FlatRow[];
  readonly presence: ReadonlyMap<string, readonly RowPresence[]>;
  readonly nav: NavState;
  readonly onNavChange: (nav: NavState) => void;
  readonly onCommand: (command: TreeCommand) => void;
  readonly onRename: (id: string, value: string) => void;
  readonly canDrop: (dragIds: readonly string[], target: DropTarget) => boolean;
  readonly onDrop: (dragIds: readonly string[], target: DropTarget) => void;
  /** The viewport height the virtualiser assumes before it can measure (server rendering, first paint). */
  readonly initialHeight?: number;
};

type DragState = { readonly ids: readonly string[]; readonly label: string };
type DropState = { readonly overId: string; readonly position: DropPosition; readonly allowed: boolean };

function DndRow(props: { readonly row: FlatRow; readonly start: number; readonly children: ReactNode; readonly onClick: (e: MouseEvent) => void; readonly onDoubleClick: () => void; readonly onContextMenu: () => void; readonly selected: boolean; readonly expandable: boolean }) {
  const { row, start, children, onClick, onDoubleClick, onContextMenu, selected, expandable } = props;
  const drag = useDraggable({ id: row.id });
  const drop = useDroppable({ id: row.id });
  return (
    <div
      ref={(el) => {
        drag.setNodeRef(el);
        drop.setNodeRef(el);
      }}
      {...drag.attributes}
      {...drag.listeners}
      // dnd-kit's attributes make the row a button; the tree semantics are what we want.
      role="treeitem"
      tabIndex={-1}
      id={`row-${row.id}`}
      data-testid="tree-row"
      data-row-id={row.id}
      aria-level={row.depth + 1}
      aria-selected={selected}
      aria-expanded={expandable ? !row.collapsed : undefined}
      aria-label={`${row.view.kind} ${displayName(row.view)}`}
      // Positioned by `top`, not a transform: dnd-kit measures rects without transforms.
      className="absolute inset-x-0"
      style={{ top: `${String(start)}px` }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
    >
      {children}
    </div>
  );
}

function pointerY(event: DragMoveEvent): number | null {
  const activator = event.activatorEvent;
  return activator instanceof MouseEvent ? activator.clientY + event.delta.y : null;
}

export function RundownTree({ rows, presence, nav, onNavChange, onCommand, onRename, canDrop, onDrop, initialHeight }: RundownTreeProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const treeRef = useRef<HTMLDivElement | null>(null);
  const nav2 = useMemo(() => navRows(rows), [rows]);
  const indexById = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows]);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dropState, setDropState] = useState<DropState | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // The rule warns that React Compiler cannot memoise around TanStack Virtual's
  // mutable instance. This build does not use React Compiler, and this is the
  // library's documented usage; nothing memoised receives the instance.
  // oxlint-disable-next-line react/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    initialRect: { width: 800, height: initialHeight ?? 600 },
  });

  const focusedIndex = nav.focusedId === null ? -1 : (indexById.get(nav.focusedId) ?? -1);
  useEffect(() => {
    if (focusedIndex >= 0) virtualizer.scrollToIndex(focusedIndex, { align: 'auto' });
  }, [focusedIndex, virtualizer]);

  const focusedRow = focusedIndex >= 0 ? (nav2[focusedIndex] ?? null) : null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const result = reduceKey(nav, nav2, { key: e.key, shift: e.shiftKey, mod: e.ctrlKey || e.metaKey, alt: e.altKey });
    if (!result.handled) return;
    e.preventDefault();
    if (result.state !== nav) onNavChange(result.state);
    if (result.command !== null) onCommand(result.command);
  };

  const clickRow = (id: string, e: MouseEvent) => {
    treeRef.current?.focus();
    onNavChange(reduceClick(nav, nav2, id, e.shiftKey, e.ctrlKey || e.metaKey));
  };

  const dragIdsFor = (activeId: string): readonly string[] => (nav.selected.includes(activeId) ? rows.filter((r) => nav.selected.includes(r.id)).map((r) => r.id) : [activeId]);

  const onDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const ids = dragIdsFor(id);
    const first = rows[indexById.get(id) ?? -1];
    setDrag({ ids, label: ids.length > 1 ? `${String(ids.length)} rows` : first === undefined ? '1 row' : displayName(first.view) });
    if (!nav.selected.includes(id)) onNavChange(reduceClick(nav, nav2, id, false, false));
  };

  const onDragMove = (event: DragMoveEvent) => {
    const over = event.over;
    const y = pointerY(event);
    if (drag === null || over === null || y === null) {
      setDropState(null);
      return;
    }
    const overId = String(over.id);
    const overRow = rows[indexById.get(overId) ?? -1];
    const draggedKind = rows[indexById.get(drag.ids[0] ?? '') ?? -1]?.view.kind;
    const canNest = overRow !== undefined && draggedKind !== undefined && NESTS[overRow.view.kind] === draggedKind;
    const position = positionFromPointer(y - over.rect.top, over.rect.height, canNest);
    if (dropState !== null && dropState.overId === overId && dropState.position === position) return;
    setDropState({ overId, position, allowed: canDrop(drag.ids, { overId, position }) });
  };

  const onDragEnd = (_event: DragEndEvent) => {
    if (drag !== null && dropState !== null && dropState.allowed) onDrop(drag.ids, { overId: dropState.overId, position: dropState.position });
    setDrag(null);
    setDropState(null);
  };

  const command = (c: TreeCommand | null) => {
    if (c !== null) onCommand(c);
  };

  const items = virtualizer.getVirtualItems();
  const dragging = new Set(drag?.ids ?? []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setDrag(null);
        setDropState(null);
      }}
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div ref={scrollRef} className="h-full overflow-auto" data-testid="rundown-tree-scroll">
            <div
              ref={treeRef}
              role="tree"
              aria-label="Rundown"
              aria-multiselectable="true"
              aria-activedescendant={nav.focusedId === null ? undefined : `row-${nav.focusedId}`}
              tabIndex={0}
              data-testid="rundown-tree"
              className="relative outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              style={{ height: `${String(Math.max(virtualizer.getTotalSize(), ROW_HEIGHT))}px` }}
              onKeyDown={onKeyDown}
            >
              {rows.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground" data-testid="tree-empty">
                  No segments yet. Press Ctrl+Enter or use “New segment” to start the rundown.
                </p>
              ) : null}
              {items.map((item) => {
                const row = rows[item.index];
                if (row === undefined) return null;
                const selected = nav.selected.includes(row.id);
                return (
                  <DndRow
                    key={row.id}
                    row={row}
                    start={item.start}
                    selected={selected}
                    expandable={row.hasChildren}
                    onClick={(e) => clickRow(row.id, e)}
                    onDoubleClick={() => onNavChange({ ...reduceClick(nav, nav2, row.id, false, false), editingId: row.id })}
                    onContextMenu={() => {
                      treeRef.current?.focus();
                      if (!selected) onNavChange(reduceClick(nav, nav2, row.id, false, false));
                    }}
                  >
                    <TreeRow
                      row={row}
                      focused={nav.focusedId === row.id}
                      selected={selected}
                      editing={nav.editingId === row.id}
                      presence={presence.get(row.id) ?? NO_PRESENCE}
                      drop={dropState !== null && dropState.overId === row.id ? { position: dropState.position, allowed: dropState.allowed } : null}
                      dragging={dragging.has(row.id)}
                      onToggleCollapse={() => onCommand({ type: 'toggle-collapse', id: row.id })}
                      onCommitName={(value) => {
                        onNavChange({ ...nav, editingId: null });
                        onRename(row.id, value);
                        treeRef.current?.focus();
                      }}
                      onCancelEdit={() => {
                        onNavChange({ ...nav, editingId: null });
                        treeRef.current?.focus();
                      }}
                    />
                  </DndRow>
                );
              })}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent data-testid="tree-context-menu">
          <ContextMenuItem onSelect={() => command(createCommandFor('segment', focusedRow, nav2))}>
            <PlusIcon /> New segment
          </ContextMenuItem>
          <ContextMenuItem disabled={createCommandFor('part', focusedRow, nav2) === null} onSelect={() => command(createCommandFor('part', focusedRow, nav2))}>
            <PlusIcon /> New part
          </ContextMenuItem>
          <ContextMenuItem disabled={createCommandFor('piece', focusedRow, nav2) === null} onSelect={() => command(createCommandFor('piece', focusedRow, nav2))}>
            <PlusIcon /> New piece
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem disabled={focusedRow === null} onSelect={() => (focusedRow === null ? undefined : onNavChange({ ...nav, editingId: focusedRow.id }))}>
            <PencilIcon /> Rename <ContextMenuShortcut>Enter</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem
            variant="destructive"
            disabled={nav.selected.length === 0}
            onSelect={() => onCommand({ type: 'delete', ids: rows.filter((r) => nav.selected.includes(r.id)).map((r) => r.id) })}
          >
            <TrashIcon /> Delete <ContextMenuShortcut>Del</ContextMenuShortcut>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <DragOverlay>
        {drag === null ? null : (
          <div className="rounded-md border bg-popover px-3 py-1 text-sm text-popover-foreground shadow-md" data-testid="drag-overlay">
            {drag.label}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
